import type { ECard } from '@graphql/generated-base';
import { useChatGPTTextCompletionQuery } from '../../../apps/admin/postOffice/PostOffice.graphql.generated';
import { AiModelVersion } from '@graphql/generated-base';
import { useState } from 'react';
import type { GiftWrapType } from '@apps/registry/guest/GuestRegistry.telemetry';
import { addError } from '@shared/utils/logger';

interface GiftNoteParams {
  prompt: string;
  fallback: string;
  skip?: boolean;
  onCompleted: (note: string, prompt: string) => void;
}

const FORBIDDEN_PHRASES = new Set([
  'husband',
  'wife',
  'bride',
  'groom',
  'mr',
  'mrs',
  'sir',
  'madam',
  'lady',
  'father',
  'mother',
  'dad',
  'mom',
  'man',
  'maid',
  'girl',
  'woman',
  'boy',
]);

export const GIFT_MESSAGE_FALLBACKS = [
  'Wishing you joy and happiness on this special occasion. Hope you love this gift!',
  "Congratulations on your special day! I'm so excited for you and hope this brings many wonderful moments.",
  'Sending you warm wishes and lots of love. I hope this gift adds to the joy of your celebration!',
  'So happy to celebrate with you! Wishing you all the best and hope you enjoy this gift.',
];

export const getRandomFallbackMessage = () => {
  return GIFT_MESSAGE_FALLBACKS[Math.floor(Math.random() * GIFT_MESSAGE_FALLBACKS.length)];
};

export const containsForbiddenPhrase = (text: string) => {
  const words = text.toLowerCase().match(/\b\w+\b/g) || [];
  return words.some(word => FORBIDDEN_PHRASES.has(word));
};

export const useGenerateGiftNote = ({ prompt, fallback, skip, onCompleted }: GiftNoteParams) => {
  const [originalNote, setOriginalNote] = useState('');
  const { loading: isGeneratedMessageLoading } = useChatGPTTextCompletionQuery({
    variables: {
      prompt,
      modelVersion: AiModelVersion.gpt35turboInstruct,
    },
    skip: skip || !!originalNote,
    batchMode: 'off',
    onCompleted(data) {
      // ChatGPT often returns noise characters at the beginning.
      // The code below trims the beginning of a string to its first capital letter and trims spaces at the end of the string.
      let i = 0;
      while (
        data.textCompletion.length > i &&
        (data.textCompletion[i] <= 'A' || data.textCompletion[i] >= 'Z') // check if it's not an uppercase letter
      ) {
        i++;
      }

      const note = data.textCompletion.substring(i).trimEnd();
      if (containsForbiddenPhrase(note)) {
        addError('Generated note contains forbidden phrases, using fallback instead.', { action: 'giftNoteForbiddenPhrase', note, prompt });
        setOriginalNote(fallback);
        onCompleted(fallback, prompt);
        return;
      }

      setOriginalNote(note);
      onCompleted(note, prompt);
    },
    onError(error) {
      addError(error, { action: 'giftNoteGenerationError', prompt });
      setOriginalNote(fallback);
      onCompleted(fallback, prompt);
    },
  });

  return { originalNote, isGeneratedMessageLoading };
};

export const prepareECardPayload = (payload: { price: number; draftId: string; theme: string; imageURL?: string; offerType: GiftWrapType }): ECard => {
  // eCard processing requires price in minor units (cents with no decimals)
  const priceInMajorUnits = payload.price * 100;
  return {
    price: priceInMajorUnits,
    emailDraftId: payload.draftId,
    quantity: 1,
    type: 'GIFT_WRAP_BASIC',
    details: [{ key: 'theme', value: payload.theme }],
    imageURL: payload.imageURL,
    offerType: payload.offerType,
  };
};

export const parseGiftWrapPayload = (payload: string) => {
  try {
    return JSON.parse(payload) as { message: string; themeId: string; image: string; store?: string; title: string; giftLink: string };
  } catch {
    return null;
  }
};

export const generateGiftWrapFallbackNote = ({ firstPartner, secondPartner, from }: { firstPartner: string; secondPartner?: string; from: string }) => {
  const partners = `${firstPartner}${secondPartner ? ` and ${secondPartner}` : ''}`;

  return `Dear ${partners},

${getRandomFallbackMessage()}

Warmly,
${from}`;
};

export const generateGiftWrapPrompt = ({
  firstPartner,
  secondPartner,
  context = 'wedding',
  from,
  giftName,
}: {
  firstPartner: string;
  secondPartner?: string;
  context?: string;
  from: string;
  giftName: string;
}) => {
  const partners = `${firstPartner}${secondPartner ? ` and ${secondPartner}` : ''}`;

  return `Write a short, first-person ${context} gift note in US English from ${from} to ${partners}.
Tone: warm and friendly.
Content: reference their ${giftName} (no mention of any relationship between the giver and recipients).
Language: inclusive—avoid gendered roles or titles (e.g., ${Array.from(FORBIDDEN_PHRASES).join(', ')}).
Pronouns: address the recipients as "you," "you both," or "you two"; avoid gendered pronouns and honorifics; avoid third-person references—if necessary, use they/them/their; do not assume a shared last name or future roles.
Names: use names exactly as provided and in this order: ${partners}.
Length: 2–4 sentences.
Prohibitions: no religious references, no emojis, no hashtags, no quotes, no sign-off or signature line.
Output: the note text only, with no headings or explanations.`;
};
